import React from 'react';
import { motion } from 'framer-motion';

interface ComparisonItem {
  name: string;
  [key: string]: any;
}

interface ComparisonVisualizationProps {
  data: ComparisonItem[];
  config: {
    title?: string;
    description?: string;
    explanation?: string;
    columns?: Array<string | { field?: string; key?: string; header?: string; label?: string }>;
    highlights?: Record<string, string>;
    labels?: Record<string, string>;
  };
  preview?: boolean;
  collapsed?: boolean;
}

const ComparisonVisualization: React.FC<ComparisonVisualizationProps> = ({
  data,
  config,
  preview = false,
  collapsed = false,
}) => {
  if (!data || data.length === 0) {
    return <div className="viz-unsupported">No comparison data provided</div>;
  }

  // Columns arrive as plain keys or as { field, header } objects
  const columns = (config.columns || Object.keys(data[0]).filter(k => k !== 'name'))
    .map(col => typeof col === 'string'
      ? { field: col, header: config.labels?.[col] || col }
      : { field: String(col.field ?? col.key ?? ''), header: String(col.header ?? col.label ?? col.field ?? col.key ?? '') })
    .filter(col => col.field);
  const items = data;

  const renderCellValue = (value: any) => {
    if (typeof value === 'boolean') {
      return value
        ? <span className="viz-compare-check">&#10003;</span>
        : <span className="viz-compare-cross">&#10007;</span>;
    }
    if (typeof value === 'number') {
      return <span className="viz-compare-number">{value.toLocaleString()}</span>;
    }
    if (value !== null && typeof value === 'object') {
      return <span>{JSON.stringify(value)}</span>;
    }
    return <span>{String(value ?? '—')}</span>;
  };

  return (
    <div className={`viz-comparison-wrapper ${preview ? 'viz-preview' : ''}`}>
      {!preview && config.title && (
        <div className="viz-comparison-header">
          <h3 className="viz-comparison-title">{config.title}</h3>
          {config.description && <p className="viz-comparison-description">{config.description}</p>}
        </div>
      )}
      <motion.div
        initial={false}
        animate={{
          height: collapsed ? 0 : 'auto',
          opacity: collapsed ? 0 : 1,
        }}
        transition={{
          height: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
          opacity: { duration: 0.2, ease: 'easeOut' },
        }}
        style={{ overflow: 'hidden' }}
      >
        <div className="viz-comparison-table-scroll">
          <table className="viz-comparison-table">
            <thead>
              <tr>
                <th className="viz-compare-corner"></th>
                {items.map((item, i) => (
                  <th key={i} className="viz-compare-item-header">
                    {String(item.name ?? '')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {columns.map(({ field: col, header }, rowIdx) => (
                <tr key={col} className={rowIdx % 2 === 0 ? 'viz-compare-row-even' : ''}>
                  <td className="viz-compare-label">
                    {header}
                  </td>
                  {items.map((item, colIdx) => {
                    const highlight = config.highlights?.[col];
                    const isHighlighted = highlight && String(item[col]) === highlight;
                    return (
                      <td
                        key={colIdx}
                        className={`viz-compare-cell ${isHighlighted ? 'viz-compare-highlighted' : ''}`}
                      >
                        {renderCellValue(item[col])}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!preview && config.explanation && (
          <div className="viz-comparison-footer">
            <span>{config.explanation}</span>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default ComparisonVisualization;
